import { useCallback, useEffect } from "react";
import { useQuery } from "urql";
import {
  GetNewsFeedDynamicFilterQuery,
  type NewsFeedDynamicSidebarResult,
} from "@/data-access/news-feeds/queries/news-feed-sidebar-filters-queries";
import { useFeature } from "@/lib/feature-management";
import { normalizeDynamicFilterItems } from "@/store/news-feed/filters/dynamic-filters.slice";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import { useAnalysisParamsContext } from "@/store/news-feed/params/analysis-params.context";
import { useAnalysisParamsForQueries } from "@/store/news-feed/params/useAnalysisParamsForQueries";

const LIST_CATEGORY = "PUBLISHER_MEDIA_LIST_ID" as const;
const GROUP_CATEGORY = "PUBLISHER_MEDIA_LIST_GROUP_ID" as const;

/**
 * Fetches media lists and their groups in a SINGLE query so both categories
 * always arrive atomically — no race between two separate requests that would
 * cause buildMediaListHierarchy to see lists before groups and orphan them
 * into the flat (ungrouped) fallback.
 */
export const useMediaListFilters = () => {
  const fetchOnExpand = useFeature("fetch-dynamic-filters-exp", false);

  const expandedCategories = useNewsFeedFilters(
    (state) => state.expandedCategories,
  );

  const analysis = useAnalysisParamsContext();

  const {
    feedId,
    startDate,
    endDate,
    selectedFilters,
    chartSelection,
    feedVolumeStartDate,
    feedVolumeEndDate,
  } = useAnalysisParamsForQueries();

  const hasSelectedMediaLists =
    (analysis?.selectedFilters[LIST_CATEGORY]?.length ?? 0) > 0 ||
    (analysis?.selectedFilters[GROUP_CATEGORY]?.length ?? 0) > 0;

  const shouldFetch =
    !fetchOnExpand ||
    expandedCategories.has(LIST_CATEGORY) ||
    hasSelectedMediaLists;

  const updateDynamicItems = useNewsFeedFilters((s) => s.updateDynamicItems);
  const setDynamicError = useNewsFeedFilters((s) => s.setDynamicError);
  const setDynamicLoading = useNewsFeedFilters((s) => s.setDynamicLoading);

  const listData = useNewsFeedFilters((state) =>
    state.dynamicFilters.get(LIST_CATEGORY),
  );
  const groupData = useNewsFeedFilters((state) =>
    state.dynamicFilters.get(GROUP_CATEGORY),
  );

  const [result, reexecute] = useQuery<NewsFeedDynamicSidebarResult>({
    query: GetNewsFeedDynamicFilterQuery,
    variables: {
      projectionFilters: {
        ...(feedId ? { feedId } : {}),
        ...(startDate && endDate ? { dateRange: { startDate, endDate } } : {}),
        state: "ADDED",
        ...(chartSelection ? { chartSelection } : {}),
        ...(chartSelection && feedVolumeStartDate && feedVolumeEndDate
          ? {
              chartSelectionDate: {
                startDate: feedVolumeStartDate,
                endDate: feedVolumeEndDate,
              },
            }
          : {}),
        ...((selectedFilters ?? {}) as Record<string, unknown>),
      },
      // Both categories in one request — they arrive together or not at all.
      categories: [LIST_CATEGORY, GROUP_CATEGORY],
    },
    pause: !shouldFetch || !feedId || !startDate || !endDate,
    requestPolicy: "cache-and-network",
  });

  // Write lists and groups to the store in a single effect so they are always
  // consistent with each other within one render cycle.
  useEffect(() => {
    if (!result.data?.newsFeedDynamicSidebar) return;

    const raw = result.data.newsFeedDynamicSidebar;

    const listItems = normalizeDynamicFilterItems(
      raw.filter((item) => item.category === LIST_CATEGORY),
    );
    const groupItems = normalizeDynamicFilterItems(
      raw.filter((item) => item.category === GROUP_CATEGORY),
    );

    updateDynamicItems(LIST_CATEGORY, listItems);
    updateDynamicItems(GROUP_CATEGORY, groupItems);
  }, [result.data, updateDynamicItems]);

  useEffect(() => {
    setDynamicLoading(LIST_CATEGORY, result.fetching);
    setDynamicLoading(GROUP_CATEGORY, result.fetching);
  }, [result.fetching, setDynamicLoading]);

  useEffect(() => {
    const msg = result.error?.message ?? null;
    setDynamicError(LIST_CATEGORY, msg);
    setDynamicError(GROUP_CATEGORY, msg);
  }, [result.error, setDynamicError]);

  const refetch = useCallback(() => {
    reexecute({ requestPolicy: "cache-and-network" });
  }, [reexecute]);

  return {
    listItems: listData?.items ?? [],
    listItemsMap: listData?.itemsMap ?? new Map(),
    groupItems: groupData?.items ?? [],
    groupItemsMap: groupData?.itemsMap ?? new Map(),
    isLoading: result.fetching,
    error: result.error?.message ?? null,
    refetch,
  };
};
